import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { EDITOR } from '@test-kit/selectors/editor';
import type { PaywallProvider, PickerTab } from '@modules/reactFlow/components/onboarding/editorSectionsV2/paywall/productTabOf';

export interface ConnectProviderBannerProps {
  rowIndex: number;
  mode: PickerTab;
  projectId: string;
  stripeReason: string | null;
  paddleReason: string | null;
  preferredProvider: PaywallProvider;
  onConnectStripe: () => void;
  disabled?: boolean;
}

/** Paddle opens settings in another tab so the unsaved editor remains intact.
 * Stripe delegates to the existing OAuth/save guard; this component only renders. */
export function ConnectProviderBanner({ rowIndex, mode, projectId, stripeReason, paddleReason,
  preferredProvider, onConnectStripe, disabled }: ConnectProviderBannerProps) {
  const stripeLabel = mode === 'test' ? 'Sandbox' : 'Live';
  const paddleLabel = mode === 'test' ? 'Sandbox' : 'Production';
  const stripeChecking = stripeReason?.startsWith('Checking');
  const paddleChecking = paddleReason?.startsWith('Checking');
  return <Box data-testid={EDITOR.paywallProductPicker.connectBanner(rowIndex)} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Typography variant="subtitle2">Connect a payment provider</Typography>
    <Typography variant="caption" color="text.secondary">
      {mode === 'test' ? 'Connect Stripe or Paddle to create test products without real charges.' : 'Connect Stripe Live or Paddle Production to create products for real payments.'}
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {stripeChecking ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={16} /><Typography variant="caption">{stripeReason}</Typography></Box> :
        <Button size="small" variant={preferredProvider === 'stripe' ? 'contained' : 'outlined'} disabled={disabled}
          data-testid={EDITOR.paywallProductPicker.connectBtn(rowIndex, mode)} onClick={onConnectStripe}>
          {`Connect ${stripeLabel} Stripe Account`}
        </Button>}
      {paddleChecking ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={16} /><Typography variant="caption">{paddleReason}</Typography></Box> :
        <Button component="a" href={`/project/${encodeURIComponent(projectId)}/integrations?open=paddle`} target="_blank" rel="noopener noreferrer"
          size="small" variant={preferredProvider === 'paddle' ? 'contained' : 'outlined'} disabled={disabled || !projectId}
          data-testid={EDITOR.paywallProductPicker.connectPaddle(rowIndex)}>
          {`Connect Paddle ${paddleLabel}`}
        </Button>}
    </Box>
    {stripeReason && !stripeChecking && <Typography variant="caption" color="text.secondary">{stripeReason}</Typography>}
    {paddleReason && !paddleChecking && <Typography variant="caption" color="text.secondary">{paddleReason}</Typography>}
  </Box>;
}
